import { supabase } from "@/shared/integrations/supabase/client";
import { sameStorageObject } from "@/shared/lib/firebaseStorageUrl";
import type { TablesUpdate } from "@/shared/integrations/supabase/types";

/**
 * `types.ts` is generated from the deployed schema, so RPCs that ship with this
 * change's migration aren't in it yet. Narrow escape hatch rather than the
 * `as any` alias used elsewhere, so this stays lint-clean.
 *
 * `.bind` is required, not decorative: SupabaseClient.rpc is `return
 * this.rest.rpc(...)`, so detaching the method leaves `this` undefined and it
 * throws "Cannot read properties of undefined (reading 'rest')" at call time.
 */
type UntypedRpc = (
  fn: string,
  args: Record<string, unknown>,
) => Promise<{ data: unknown; error: { message: string } | null }>;
const rpc = supabase.rpc.bind(supabase) as unknown as UntypedRpc;

export interface DeleteAssetPhotoInput {
  /** `asset_documents.id`. */
  photoId: string;
  /** `property_assets.id` the photo belongs to. */
  assetId: string;
  /** The photo's stored `file_path`, used to detect stale asset columns. */
  filePath: string;
}

/**
 * Move an asset photo to the recycle bin.
 *
 * Soft, not hard: the Firebase sync re-inserts any photo whose identity it can't
 * find on the asset, and the photo still exists on the Firestore form node — a
 * hard-deleted row would come back on that asset's next sync. Keeping the row
 * with `deleted_at` set leaves the identity in place so the sync skips it.
 *
 * Goes through an RPC because that is where authorization lives: RLS is
 * disabled on asset_documents, so the table's own policies are inert and
 * `can_manage_property` inside the SECURITY DEFINER function is the only check.
 *
 * The Storage object is deliberately left alone: the Firestore node,
 * robotic-imaging-web and the Flutter app all reference the same bytes.
 */
export async function deleteAssetPhoto({
  photoId,
  assetId,
  filePath,
}: DeleteAssetPhotoInput): Promise<void> {
  const { error } = await rpc("soft_delete_asset_document", { p_id: photoId });
  if (error) throw error;

  // Don't leave the asset pointing at a photo that no longer appears anywhere.
  // Compared by object because these columns routinely hold a different token
  // for the same object than the document row does.
  const { data, error: readError } = await supabase
    .from("property_assets")
    .select("asset_photo_url, nameplate_photo_url")
    .eq("id", assetId)
    .maybeSingle();
  if (readError) throw readError;

  const patch: TablesUpdate<"property_assets"> = {};
  if (sameStorageObject(data?.asset_photo_url, filePath)) patch.asset_photo_url = null;
  if (sameStorageObject(data?.nameplate_photo_url, filePath)) patch.nameplate_photo_url = null;
  if (Object.keys(patch).length === 0) return;

  const { error: updateError } = await supabase
    .from("property_assets")
    .update(patch)
    .eq("id", assetId);
  if (updateError) throw updateError;
}

/** Restore a photo from the recycle bin. RPC for the same RLS reason. */
export async function restoreAssetPhoto(photoId: string): Promise<void> {
  const { error } = await rpc("restore_asset_document", { p_id: photoId });
  if (error) throw error;
}
